import { useEffect, useSyncExternalStore } from "react";
import { findHelpTopic } from "@/help/content";

/**
 * 방금까지 보던 화면들.
 *
 * **왜 필요한가** — 등록하다 말고 잠깐 다른 화면을 보고 오는 일이 흔한데(담당자 요청
 * 2026-09-09), 돌아오려면 사이드바에서 메뉴를 다시 찾아야 했다. 상단바에 최근 화면을
 * 칩으로 늘어놓아 한 번에 되돌아가게 한다.
 *
 * **이름은 새로 짓지 않는다.** `findHelpTopic` 이 `/admin/dispatch/:id/operation` 같은 동적
 * 경로까지 `운행 회차 관리` 로 풀어 준다 — 도움말이 이미 화면마다 제목을 갖고 있으므로
 * 그것을 그대로 쓴다. 못 찾으면 부르는 쪽이 사이드바 메뉴 라벨로 메운다.
 *
 * 값은 모듈 전역이다. `useStickyState` 와 같은 이유로 `localStorage` 를 쓰지 않는다 —
 * 새로고침하면 사라지는 것이 맞다.
 */

export interface RecentScreen {
  /** 이동할 곳. 쿼리를 포함한다 — 송장 제출현황은 일자를 URL 에 들고 있다 */
  href: string;
  /** 중복 판정 기준. 같은 화면을 쿼리만 바꿔 여러 번 담지 않는다 */
  path: string;
  title: string;
}

/**
 * 상단바에 보이는 것은 화면 폭에 따라 3~6개다(`AdminLayout` 의 `RECENT_MAX`).
 * 지금 화면 하나가 빠지므로 그보다 넉넉히 들고 있는다.
 */
const KEEP = 10;

let screens: RecentScreen[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): RecentScreen[] {
  return screens;
}

/**
 * 지금 화면을 기록한다.
 *
 * 같은 경로가 이미 있으면 **빼고 맨 앞에 다시 넣는다** — 쿼리가 바뀌었으면 최신 것으로
 * 갱신돼야 한다. 배차 등록이 `?new=1` 을 지울 때 주소를 `replace` 로 갈므로,
 * 그 갱신이 여기까지 흘러와 칩이 깨끗한 주소를 가리키게 된다.
 */
export function recordScreen(entry: RecentScreen) {
  const head = screens[0];
  if (head && head.path === entry.path && head.href === entry.href) return;
  screens = [entry, ...screens.filter((item) => item.path !== entry.path)].slice(0, KEEP);
  emit();
}

export function clearRecentScreens() {
  if (screens.length === 0) return;
  screens = [];
  emit();
}

/**
 * 지금 화면을 기록하고, **그 화면을 뺀** 최근 목록을 돌려준다.
 *
 * 지금 보고 있는 곳을 칩으로 내밀 이유가 없다 — 누를 데가 없는 버튼이 된다.
 */
export function useRecentScreens(
  pathname: string,
  search: string,
  fallbackTitle: (pathname: string) => string | undefined
): RecentScreen[] {
  const all = useSyncExternalStore(subscribe, snapshot, snapshot);

  useEffect(() => {
    const title = findHelpTopic(pathname)?.title ?? fallbackTitle(pathname);
    // 이름을 못 붙이는 화면은 담지 않는다. 경로만 적힌 칩은 담당자에게 아무 뜻이 없다.
    if (!title) return;
    recordScreen({ href: `${pathname}${search}`, path: pathname, title });
  }, [pathname, search, fallbackTitle]);

  return all.filter((item) => item.path !== pathname);
}

export default useRecentScreens;
